// =============================================================================
// VINUM - Favorites Store
// Preferiti (wishlist) sincronizzati lato server.
// Tiene un Set di itemId; il toggle aggiorna localmente in modo ottimistico
// e poi chiama il backend, con rollback in caso di errore.
// Persistenza locale (localStorage) come snapshot offline; il server resta
// la source of truth e viene ricaricato al login (loadFavorites).
// =============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { FavoritesAPI } from '../api';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface FavoritesState {
  ids: string[];
  isLoading: boolean;
  error: string | null;
}

interface FavoritesActions {
  loadFavorites: () => Promise<void>;
  toggleFavorite: (itemId: string) => void;
  isFavorite: (itemId: string) => boolean;
  clearFavorites: () => void;
}

type FavoritesStore = FavoritesState & FavoritesActions;

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

export const useFavoritesStore = create<FavoritesStore>()(
  persist(
    (set, get) => ({
      ids: [],
      isLoading: false,
      error: null,

      // Carica i preferiti dal server (source of truth).
      loadFavorites: async () => {
        set({ isLoading: true, error: null });
        try {
          const ids = await FavoritesAPI.list();
          set({ ids, isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Errore caricamento preferiti';
          set({ isLoading: false, error: message });
        }
      },

      // Aggiunge/rimuove un preferito in modo ottimistico, poi sincronizza.
      toggleFavorite: (itemId: string) => {
        const current = get().ids;
        const isFav = current.includes(itemId);
        const optimistic = isFav
          ? current.filter((id) => id !== itemId)
          : [itemId, ...current];

        set({ ids: optimistic, error: null });

        const request = isFav
          ? FavoritesAPI.remove(itemId)
          : FavoritesAPI.add(itemId);

        request.catch((error) => {
          console.error('Error toggling favorite:', error);
          // Rollback
          set({ ids: current, error: 'Errore aggiornamento preferiti' });
        });
      },

      isFavorite: (itemId: string) => get().ids.includes(itemId),

      clearFavorites: () => set({ ids: [], error: null }),
    }),
    {
      name: 'favorites_item_ids',
      storage: createJSONStorage(() => {
        if (typeof window !== 'undefined' && window.localStorage) {
          return localStorage;
        }
        return {
          getItem: () => null,
          setItem: () => {},
          removeItem: () => {},
        };
      }),
      partialize: (state) => ({ ids: state.ids }),
    }
  )
);

// -----------------------------------------------------------------------------
// Selectors
// -----------------------------------------------------------------------------

export const selectFavoriteIds = (state: FavoritesStore) => state.ids;
export const selectFavoritesCount = (state: FavoritesStore) => state.ids.length;
export const selectFavoritesLoading = (state: FavoritesStore) => state.isLoading;
